import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { createPageUrl } from '@/utils';
import { Bot, CheckCircle, Play, Brain, ChevronRight, ArrowRight, Clock, MessageSquare, Zap, Loader2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { format } from 'date-fns';
import BotClarificationChatModal from '@/components/bots/BotClarificationChatModal';

export default function BotsManagementSection({ 
  currentUser, 
  officers, 
  dailyTasks,
  activities,
  getOfficerDisplay 
}) {
  const queryClient = useQueryClient();
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [selectedTaskForChat, setSelectedTaskForChat] = useState(null);

  // Fetch pending bot memory reviews for bots owned by current user
  const { data: pendingMemoryReviews = [] } = useQuery({
    queryKey: ['pendingBotMemoryReviews', currentUser?.email],
    queryFn: async () => {
      // Get bots owned by current user
      const myBots = officers.filter(o => o.is_bot && o.bot_owner_email === currentUser?.email);
      if (myBots.length === 0) return [];
      
      // Get pending feedback entries for these bots
      const allMemories = await base44.entities.BotMemory.list();
      return allMemories.filter(m => 
        m.type === 'OWNER_FEEDBACK' && 
        m.owner_review_status === 'pending' &&
        myBots.some(b => b.id === m.bot_profile_id)
      ).map(m => ({
        ...m,
        bot: myBots.find(b => b.id === m.bot_profile_id)
      }));
    },
    enabled: !!currentUser?.email && officers.length > 0
  });

  // Get bot tasks pending approval (status = 'pending_approval' and generated_by_ai = true)
  const botTasksPendingApproval = dailyTasks.filter(t => 
    t.generated_by_ai && 
    t.status === 'pending_approval' &&
    officers.some(o => o.is_bot && o.user_email === t.user_email && o.bot_owner_email === currentUser?.email)
  );

  // Get bot tasks created but never run (bot_execution_status = 'not_started')
  const botTasksNeverRun = dailyTasks.filter(t => 
    t.status === 'approved' &&
    t.bot_execution_status === 'not_started' &&
    officers.some(o => o.is_bot && o.user_email === t.user_email && o.bot_owner_email === currentUser?.email)
  );

  // Mutations
  const approveTaskMutation = useMutation({
    mutationFn: (taskId) => base44.entities.DailyTask.update(taskId, { status: 'approved' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dailyTasks'] })
  });

  const runBotTaskMutation = useMutation({
    mutationFn: async (task) => {
      // Update status to in_progress
      await base44.entities.DailyTask.update(task.id, { bot_execution_status: 'in_progress' });
      // Invoke the bot execution function
      await base44.functions.invoke('executeBotTask', { task_id: task.id });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['dailyTasks'] })
  });

  // Process all bot learnings — discovers all unprocessed dates automatically
  const processLearningsMutation = useMutation({
    mutationFn: async () => {
      // Fetch all WORKING_TASK entries for bots owned by current user
      const myBots = officers.filter(o => o.is_bot && o.bot_owner_email === currentUser?.email);
      const allWorkingTasks = await base44.entities.BotMemory.list();
      const workingTasks = allWorkingTasks.filter(m => 
        m.type === 'WORKING_TASK' && myBots.some(b => b.id === m.bot_profile_id)
      );
      
      // Get unique dates from working tasks + today + yesterday
      const today = new Date().toISOString().split('T')[0];
      const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
      const taskDates = [...new Set(workingTasks.map(t => t.date))];
      const allDates = [...new Set([today, yesterday, ...taskDates])].sort().reverse();
      
      const results = [];
      for (const date of allDates) {
        try {
          const res = await base44.functions.invoke('processBotDailyMemory', { date_override: date });
          results.push({ date, ...res.data });
        } catch (err) {
          results.push({ date, error: err.message });
        }
      }
      return results;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pendingBotMemoryReviews'] });
      queryClient.invalidateQueries({ queryKey: ['botMemory'] });
      queryClient.invalidateQueries({ queryKey: ['pendingFeedback'] });
    }
  });

  // Handle running a bot task - opens chat modal for interaction
  const handleRunBotTask = (task) => {
    setSelectedTaskForChat(task);
    setChatModalOpen(true);
  };

  // Handle chat execution complete
  const handleChatExecutionComplete = () => {
    queryClient.invalidateQueries({ queryKey: ['dailyTasks'] });
  };

  const totalItems = botTasksPendingApproval.length + botTasksNeverRun.length + pendingMemoryReviews.length;

  // Get bot info for a task
  const getBotForTask = (task) => {
    return officers.find(o => o.is_bot && o.user_email === task.user_email);
  };

  // Get activity info for a task
  const getActivityForTask = (task) => {
    return activities.find(a => a.id === task.activity_id);
  };

  if (totalItems === 0) {
    return null;
  }

  return (
    <Collapsible>
      <CollapsibleTrigger className="w-full mb-3 pl-1 flex items-center justify-between hover:opacity-80 transition-opacity">
        <h3 className="text-amber-500 text-base font-extrabold uppercase tracking-wider">
          VI. BOTS MANAGEMENT ({totalItems})
        </h3>
        <ChevronRight className="w-5 h-5 text-amber-500 transition-transform ui-expanded:rotate-90" />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-3">

          {/* Process Learnings Action */}
          <div className="flex items-center justify-between p-3 rounded-lg bg-indigo-50 border border-indigo-200">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-indigo-500" />
              <span className="text-sm text-indigo-700 font-medium">Consolidate bot work into learnings</span>
            </div>
            <Button
              size="sm"
              onClick={() => processLearningsMutation.mutate()}
              disabled={processLearningsMutation.isPending}
              className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700"
            >
              {processLearningsMutation.isPending ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Processing...</>
              ) : (
                <><Zap className="w-3 h-3 mr-1" /> Process Learnings</>
              )}
            </Button>
          </div>
          
          {/* Bot Tasks Pending Approval */}
          <Collapsible>
            <div className={`rounded-lg border ${botTasksPendingApproval.length > 0 ? 'bg-purple-50 border-purple-500/30' : 'bg-white border-slate-200'}`}>
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-purple-500" />
                  <div className="text-left">
                    <h3 className="text-slate-900 font-semibold">Bot Tasks Pending Approval</h3>
                    <p className="text-xs text-slate-500">AI-generated tasks awaiting your approval</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={botTasksPendingApproval.length > 0 ? "bg-purple-500/20 text-purple-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                    {botTasksPendingApproval.length}
                  </Badge>
                  <ChevronRight className="w-5 h-5 text-slate-400 transition-transform" />
                </div>
              </CollapsibleTrigger>
              {botTasksPendingApproval.length > 0 && (
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                    {botTasksPendingApproval.map((task) => {
                      const bot = getBotForTask(task);
                      const activity = getActivityForTask(task);
                      return (
                        <div key={task.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Bot className="w-4 h-4 text-purple-500" />
                                <span className="text-xs font-medium text-purple-600">{bot?.callsign || 'Unknown Bot'}</span>
                              </div>
                              <div className="text-sm font-medium text-slate-900 mb-1">{task.task_description}</div>
                              <div className="text-xs text-slate-500">
                                {activity && <span>[{activity.activity_code}] </span>}
                                {format(new Date(task.date), 'MMM d')}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                                onClick={() => approveTaskMutation.mutate(task.id)}
                                disabled={approveTaskMutation.isPending}
                              >
                                <CheckCircle className="w-3 h-3 mr-1" /> Approve
                              </Button>
                              <Link to={createPageUrl(`dailytasks?highlight=${task.id}`)}>
                                <Button size="sm" variant="ghost" className="h-7 text-xs">
                                  <ArrowRight className="w-3 h-3" />
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              )}
            </div>
          </Collapsible>

          {/* Bot Tasks Never Run */}
          <Collapsible>
            <div className={`rounded-lg border ${botTasksNeverRun.length > 0 ? 'bg-cyan-50 border-cyan-500/30' : 'bg-white border-slate-200'}`}>
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                <div className="flex items-center gap-3">
                  <Play className="w-5 h-5 text-cyan-500" />
                  <div className="text-left">
                    <h3 className="text-slate-900 font-semibold">Bot Tasks Ready to Run</h3>
                    <p className="text-xs text-slate-500">Approved tasks that haven't been executed yet</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={botTasksNeverRun.length > 0 ? "bg-cyan-500/20 text-cyan-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                    {botTasksNeverRun.length}
                  </Badge>
                  <ChevronRight className="w-5 h-5 text-slate-400 transition-transform" />
                </div>
              </CollapsibleTrigger>
              {botTasksNeverRun.length > 0 && (
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                    {botTasksNeverRun.map((task) => {
                      const bot = getBotForTask(task);
                      const activity = getActivityForTask(task);
                      return (
                        <div key={task.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Bot className="w-4 h-4 text-cyan-500" />
                                <span className="text-xs font-medium text-cyan-600">{bot?.callsign || 'Unknown Bot'}</span>
                              </div>
                              <div className="text-sm font-medium text-slate-900 mb-1">{task.task_description}</div>
                              <div className="text-xs text-slate-500">
                                {activity && <span>[{activity.activity_code}] </span>}
                                {format(new Date(task.date), 'MMM d')}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                className="h-7 text-xs bg-cyan-600 hover:bg-cyan-700"
                                onClick={() => handleRunBotTask(task)}
                                disabled={runBotTaskMutation.isPending}
                              >
                                <MessageSquare className="w-3 h-3 mr-1" /> Run
                              </Button>
                              <Link to={createPageUrl(`dailytasks?highlight=${task.id}`)}>
                                <Button size="sm" variant="ghost" className="h-7 text-xs">
                                  <ArrowRight className="w-3 h-3" />
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              )}
            </div>
          </Collapsible>

          {/* Pending Memory Reviews */}
          <Collapsible>
            <div className={`rounded-lg border ${pendingMemoryReviews.length > 0 ? 'bg-amber-50 border-amber-500/30' : 'bg-white border-slate-200'}`}>
              <CollapsibleTrigger className="w-full p-4 flex items-center justify-between hover:bg-slate-100/50 transition-colors">
                <div className="flex items-center gap-3">
                  <Brain className="w-5 h-5 text-amber-500" />
                  <div className="text-left">
                    <h3 className="text-slate-900 font-semibold">Pending Memory Reviews</h3>
                    <p className="text-xs text-slate-500">Bot learnings awaiting your review</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={pendingMemoryReviews.length > 0 ? "bg-amber-500/20 text-amber-500 border-none" : "bg-slate-200 text-slate-600 border-none"}>
                    {pendingMemoryReviews.length}
                  </Badge>
                  <ChevronRight className="w-5 h-5 text-slate-400 transition-transform" />
                </div>
              </CollapsibleTrigger>
              {pendingMemoryReviews.length > 0 && (
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-2 max-h-60 overflow-y-auto">
                    {pendingMemoryReviews.map((memory) => (
                      <div key={memory.id} className="p-3 bg-white rounded border border-slate-200 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Bot className="w-4 h-4 text-amber-500" />
                              <span className="text-xs font-medium text-amber-600">{memory.bot?.callsign || 'Unknown Bot'}</span>
                            </div>
                            <div className="text-sm font-medium text-slate-900 mb-1">
                              Learnings from {format(new Date(memory.date), 'MMM d, yyyy')}
                            </div>
                            <div className="text-xs text-slate-500 line-clamp-2">
                              {memory.content?.substring(0, 100)}...
                            </div>
                          </div>
                          <Link to={createPageUrl(`profile?officer=${memory.bot?.id}&section=memory`)}>
                            <Button
                              size="sm"
                              className="h-7 text-xs bg-amber-600 hover:bg-amber-700"
                            >
                              <Brain className="w-3 h-3 mr-1" /> Review
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              )}
            </div>
          </Collapsible>

        </div>
      </CollapsibleContent>

      {/* Bot Chat Modal */}
      <BotClarificationChatModal
        open={chatModalOpen}
        onOpenChange={setChatModalOpen}
        task={selectedTaskForChat}
        onExecutionComplete={handleChatExecutionComplete}
      />
    </Collapsible>
  );
}